/** A streamed pack-ice field with persistent openings and a bounded fragment pool. */
import { BufferGeometry, DynamicDrawUsage, Float32BufferAttribute, Group, InstancedMesh, Mesh, MeshStandardMaterial, Object3D, Vector3 } from 'three/webgpu'
import type { Vessel } from '../physics/Vessel.ts'
import { sampleWaterHeight } from './waves.ts'

const RADIUS=13, SPACING_X=Math.sqrt(3)*RADIUS, SPACING_Z=1.5*RADIUS
// Keep a broad horizon of pack ice around the ship. The field is still
// streamed around the moving vessel, so this increases the visible reach
// without allocating an unbounded world.
const RANGE=36, FRAGMENTS=360
interface Floe { key:string; x:number; z:number; variant:number; polygon:number[][] }
interface Fragment { x:number; z:number; vx:number; vz:number; age:number; spin:number; scale:number }
const hash=(x:number,z:number):number=>{ let n=Math.imul(x,374761393)^Math.imul(z,668265263);n=Math.imul(n^(n>>>13),1274126177);return ((n^(n>>>16))>>>0)/4294967296 }

function iceGeometry(): BufferGeometry {
  // Six unequal shoulders make the chipped edges less mechanically regular.
  const positions:number[]=[], colours:number[]=[]
  const points=Array.from({length:6},(_,i)=>{const a=Math.PI/6+i*Math.PI/3;const r=RADIUS*(.89+hash(i,53)*.09);return [Math.cos(a)*r,Math.sin(a)*r]})
  const tri=(a:number[],b:number[],c:number[],colour:number[])=>{positions.push(...a,...b,...c);for(let i=0;i<3;i++)colours.push(...colour)}
  for(let i=0;i<6;i++) {
    const a=points[i],b=points[(i+1)%6]
    tri([0,.7,0],[b[0],.55,b[1]],[a[0],.55,a[1]],[.83+i*.015,.91+i*.008,.94])
    tri([a[0],.55,a[1]],[b[0],.55,b[1]],[b[0],-.75,b[1]],[.32,.59,.67])
    tri([a[0],.55,a[1]],[b[0],-.75,b[1]],[a[0],-.75,a[1]],[.32,.59,.67])
  }
  const geometry=new BufferGeometry();geometry.setAttribute('position',new Float32BufferAttribute(positions,3));geometry.setAttribute('color',new Float32BufferAttribute(colours,3));geometry.computeVertexNormals();return geometry
}

export class IceField {
  readonly group=new Group()
  readonly floes=new Map<string,Floe>()
  readonly broken=new Set<string>()
  brokenCount=0
  private readonly shards:Fragment[]=[]
  private readonly geometry=iceGeometry()
  private readonly material=new MeshStandardMaterial({color:'#ffffff',vertexColors:true,roughness:.61,metalness:.04})
  private readonly sheetGeometry=new BufferGeometry()
  private readonly sheet=new Mesh(this.sheetGeometry,this.material)
  private dirty=true
  private readonly debris=new InstancedMesh(this.geometry,this.material,FRAGMENTS)
  private readonly transform=new Object3D()
  private centerQ=Infinity
  private centerR=Infinity
  private disposed=false

  constructor() {
    this.group.name='breakable-pack-ice'
    this.debris.instanceMatrix.setUsage(DynamicDrawUsage)
    this.sheet.frustumCulled=false;this.debris.frustumCulled=false;this.debris.count=0
    this.group.add(this.sheet,this.debris)
    this.stream(new Vector3())
  }

  private stream(position:Vector3):void {
    const r=Math.round(position.z/SPACING_Z),q=Math.round(position.x/SPACING_X-r*.5)
    if(q===this.centerQ&&r===this.centerR)return
    this.centerQ=q;this.centerR=r;this.floes.clear();this.dirty=true
    for(let dr=-RANGE;dr<=RANGE;dr++)for(let dq=-RANGE;dq<=RANGE;dq++) {
      const qr=q+dq,rr=r+dr,key=`${qr}:${rr}`
      const [x,z]=this.center(qr,rr)
      // A clear departure pocket and occasional natural leads through the pack.
      if((Math.abs(x)<34&&z>-85&&z<75)||this.broken.has(key)||hash(qr,rr)<.055)continue
      this.floes.set(key,{key,x,z,variant:Math.floor(hash(qr+11,rr)*6),polygon:this.polygon(qr,rr)})
    }
  }

  private center(q:number,r:number):number[] {
    return [(q+r*.5)*SPACING_X+(hash(q+31,r)-.5)*RADIUS*.9,r*SPACING_Z+(hash(q,r+67)-.5)*RADIUS*.9]
  }

  /** Voronoi fracture cells from jittered centres: adjacent pieces share natural, irregular edges. */
  private polygon(q:number,r:number):number[][] {
    const [cx,cz]=this.center(q,r)
    let polygon=[[-RADIUS*3,-RADIUS*3],[RADIUS*3,-RADIUS*3],[RADIUS*3,RADIUS*3],[-RADIUS*3,RADIUS*3]]
    for(let dr=-2;dr<=2;dr++)for(let dq=-2;dq<=2;dq++) {
      if(!dq&&!dr)continue
      const [nx,nz]=this.center(q+dq,r+dr),dx=nx-cx,dz=nz-cz,limit=(dx*dx+dz*dz)/2
      const clipped:number[][]=[]
      for(let i=0;i<polygon.length;i++) {
        const a=polygon[i],b=polygon[(i+1)%polygon.length]
        const da=a[0]*dx+a[1]*dz-limit,db=b[0]*dx+b[1]*dz-limit
        if(da<=0)clipped.push(a)
        if((da<0)!==(db<0)){const t=da/(da-db);clipped.push([a[0]+(b[0]-a[0])*t,a[1]+(b[1]-a[1])*t])}
      }
      polygon=clipped
    }
    return polygon.map(([x,z])=>[x*.97,z*.97])
  }

  step(dt:number,_elapsed:number,ship:Vessel):number {
    if(this.disposed)return 0
    this.stream(ship.position)
    const sin=Math.sin(ship.yaw),cos=Math.cos(ship.yaw),halfBeam=ship.config.beam/2,halfLength=ship.config.length/2
    let hits=0
    const solid=[]
    for(const floe of this.floes.values()) {
      const dx=floe.x-ship.position.x,dz=floe.z-ship.position.z
      if(Math.abs(dx)>halfLength+RADIUS*2||Math.abs(dz)>halfLength+RADIUS*2)continue
      const x=dx*cos-dz*sin,z=dx*sin+dz*cos
      const nose=halfLength*.88
      const bowContact=Math.hypot(x,Math.max(0,z-nose))<halfBeam+RADIUS*.8&&z>halfLength*.25&&z<nose+RADIUS+halfBeam*.7
      // The bow does the breaking. Reversing into an intact floe behaves as contact.
      if(ship.forwardSpeed>.45&&bowContact) {
        this.dirty=true;this.floes.delete(floe.key);this.broken.add(floe.key);this.brokenCount++;hits++
        for(let i=0;i<6;i++) {
          const side=i<3?-1:1, a=i*Math.PI/3
          if(this.shards.length>=FRAGMENTS)this.shards.shift()
          this.shards.push({x:floe.x+Math.cos(a)*4,z:floe.z+Math.sin(a)*4,
            vx:cos*side*(1.5+ship.speed*.22)+sin*ship.speed*.15,
            vz:-sin*side*(1.5+ship.speed*.22)+cos*ship.speed*.15,
            age:0,spin:side*(.3+i*.05),scale:.21+hash(i,this.brokenCount)*.17})
        }
      } else if(Math.abs(x)<halfBeam+RADIUS*.65&&Math.abs(z)<halfLength+RADIUS*.65) {
        solid.push({x:floe.x,z:floe.z,halfWidth:RADIUS*.6,halfLength:RADIUS*.6,rotation:0})
      }
    }
    if(hits) {
      // Fracturing costs momentum without trapping a steadily driven icebreaker.
      const loss=Math.pow(.985,hits);ship.velocity.x*=loss;ship.velocity.z*=loss
    }
    if(solid.length)ship.resolveCollisions(solid,.03)
    for(let i=this.shards.length-1;i>=0;i--) {
      const p=this.shards[i];p.age+=dt;p.x+=p.vx*dt;p.z+=p.vz*dt;p.vx*=Math.exp(-dt*.12);p.vz*=Math.exp(-dt*.12)
      if(p.age>24)this.shards.splice(i,1)
    }
    // Keep memory bounded after very long trips; the visible/nearby channel stays intact.
    if(this.broken.size>24000)for(const key of this.broken){const [bq,br]=key.split(':').map(Number);if(Math.abs(bq-this.centerQ)>RANGE*3||Math.abs(br-this.centerR)>RANGE*3)this.broken.delete(key);if(this.broken.size<=18000)break}
    return hits
  }

  frame(elapsed:number):void {
    if(this.dirty) {
      const positions:number[]=[],colours:number[]=[]
      const tri=(a:number[],b:number[],c:number[],colour:number[])=>{positions.push(...a,...b,...c);for(let i=0;i<3;i++)colours.push(...colour)}
      for(const f of this.floes.values()) {
        const top=.62+hash(f.variant,Math.floor(f.x))*.22
        for(let i=0;i<f.polygon.length;i++) {
          const a=f.polygon[i],b=f.polygon[(i+1)%f.polygon.length]
          const av=[f.x+a[0],top,f.z+a[1]],bv=[f.x+b[0],top,f.z+b[1]]
          tri([f.x,top+.09,f.z],bv,av,[.84+f.variant*.02,.92+f.variant*.01,.98])
          tri(av,bv,[bv[0],-.7,bv[2]],[.38,.64,.73])
          tri(av,[bv[0],-.7,bv[2]],[av[0],-.7,av[2]],[.38,.64,.73])
        }
      }
      // Replace only when a cell breaks or a new ring streams in; intact pack is still.
      this.sheetGeometry.dispose()
      this.sheetGeometry.setAttribute('position',new Float32BufferAttribute(positions,3))
      this.sheetGeometry.setAttribute('color',new Float32BufferAttribute(colours,3))
      this.sheetGeometry.deleteAttribute('normal');this.sheetGeometry.computeVertexNormals()
      this.dirty=false
    }
    this.shards.forEach((p,i)=>{
      const o=this.transform,fade=Math.min(1,(24-p.age)/5)
      o.position.set(p.x,sampleWaterHeight(p.x,p.z,elapsed)+.15,p.z)
      o.rotation.set(Math.sin(p.age*2+i)*.12,p.spin*p.age,Math.cos(p.age+i)*.18)
      o.scale.set(p.scale*fade,.55*fade,p.scale*fade);o.updateMatrix();this.debris.setMatrixAt(i,o.matrix)
    })
    this.debris.count=this.shards.length;this.debris.instanceMatrix.needsUpdate=true
  }
  dispose():void {this.disposed=true;this.group.removeFromParent();this.sheetGeometry.dispose();this.debris.dispose();this.geometry.dispose();this.material.dispose();this.group.clear();this.floes.clear();this.broken.clear();this.shards.length=0}
}
